import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, Plus, Edit3, Trash2, ArrowLeft, Calendar,
  Heart, Sparkles, Target, Save, X, Filter, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const reflectionTypes = [
  { value: 'general', label: 'General Reflection', icon: BookOpen },
  { value: 'practice', label: 'Practice Reflection', icon: Heart },
  { value: 'module', label: 'Module Reflection', icon: Target },
  { value: 'gratitude', label: 'Gratitude', icon: Sparkles },
  { value: 'prayer', label: 'Prayer/Meditation', icon: Heart }
];

const moods = [
  { value: 'grounded', label: 'Grounded', emoji: '🌳' },
  { value: 'hopeful', label: 'Hopeful', emoji: '✨' },
  { value: 'uncertain', label: 'Uncertain', emoji: '🤔' },
  { value: 'struggling', label: 'Struggling', emoji: '💪' },
  { value: 'grateful', label: 'Grateful', emoji: '🙏' }
];

export default function ReflectionJournal() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    reflection_type: 'general',
    mood: null,
    is_private: true
  });

  const fetchData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const [entriesRes, statsRes] = await Promise.all([
        fetch(`${API}/api/faith/journal?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/faith/journal/stats/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setEntries(data.entries);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (err) {
      console.error('Error fetching journal:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      reflection_type: 'general',
      mood: null,
      is_private: true
    });
    setEditingEntry(null);
  };

  const handleSave = async () => {
    if (!formData.content.trim()) {
      toast.error('Please write something in your reflection');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingEntry 
        ? `${API}/api/faith/journal/${editingEntry.id}`
        : `${API}/api/faith/journal`;
      
      const res = await fetch(url, {
        method: editingEntry ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingEntry ? 'Entry updated!' : 'Reflection saved!');
        setShowEditor(false);
        resetForm();
        fetchData();
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save reflection');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title || '',
      content: entry.content,
      reflection_type: entry.reflection_type,
      mood: entry.mood,
      is_private: entry.is_private
    });
    setShowEditor(true);
  };

  const handleDelete = async (entryId) => {
    if (!confirm('Delete this reflection?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/faith/journal/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Entry deleted');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const filteredEntries = filter === 'all' 
    ? entries 
    : entries.filter(e => e.reflection_type === filter);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <BookOpen className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-stone-800 mb-2">Reflection Journal</h2>
            <p className="text-stone-600 mb-6">Log in to start your reflection practice.</p>
            <Link to="/login">
              <Button className="bg-amber-600 hover:bg-amber-700">Log In</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50/30" data-testid="reflection-journal">
      {/* Header */}
      <div className="bg-gradient-to-br from-stone-800 to-amber-900 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/faith" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Faith Hub
          </Link>
          
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-4 bg-white/20 rounded-xl">
                <BookOpen className="h-10 w-10" />
              </div>
              <div>
                <Badge className="mb-2 bg-white/20 text-white border-0">Spiritual Practice</Badge>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">Reflection Journal</h1>
                <p className="text-stone-200">
                  A space to process, reflect, and grow.
                </p>
              </div>
            </div>
            <Button 
              onClick={() => { resetForm(); setShowEditor(true); }}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Plus className="h-4 w-4 mr-2" /> New Entry
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Stats */}
        {stats && (
          <Card className="mb-8">
            <CardContent className="py-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-amber-600">{stats.total_entries}</div>
                  <p className="text-sm text-stone-600">Total Entries</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-600">{stats.current_streak}</div>
                  <p className="text-sm text-stone-600">Day Streak</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-600">
                    {stats.by_mood?.grateful || 0}
                  </div>
                  <p className="text-sm text-stone-600">Grateful Moments</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-600">
                    {Object.keys(stats.by_type || {}).length}
                  </div>
                  <p className="text-sm text-stone-600">Reflection Types</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filter === 'all' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-stone-600 border hover:bg-stone-50'
            }`}
          >
            All
          </button>
          {reflectionTypes.map(type => (
            <button
              key={type.value}
              onClick={() => setFilter(type.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === type.value 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-white text-stone-600 border hover:bg-stone-50'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Entries */}
        <div className="space-y-4">
          <AnimatePresence>
            {filteredEntries.map((entry, idx) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {reflectionTypes.find(t => t.value === entry.reflection_type)?.label || entry.reflection_type}
                        </Badge>
                        {entry.mood && (
                          <span className="text-lg">
                            {moods.find(m => m.value === entry.mood)?.emoji}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-stone-400">
                        <Calendar className="h-4 w-4" />
                        <span className="text-sm">{formatDate(entry.created_at)}</span>
                      </div>
                    </div>
                    
                    {entry.title && (
                      <h3 className="font-semibold text-stone-800 mb-2">{entry.title}</h3>
                    )}
                    
                    <p className="text-stone-600 whitespace-pre-wrap line-clamp-4">
                      {entry.content}
                    </p>

                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)}>
                        <Edit3 className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>

          {filteredEntries.length === 0 && (
            <Card className="text-center py-12">
              <CardContent>
                <BookOpen className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-stone-700 mb-2">No reflections yet</h3>
                <p className="text-stone-500 mb-4">Start your reflection practice today.</p>
                <Button 
                  onClick={() => { resetForm(); setShowEditor(true); }}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <Plus className="h-4 w-4 mr-2" /> Write First Entry
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Editor Modal */}
      <Dialog open={showEditor} onOpenChange={(open) => { if (!open) { resetForm(); } setShowEditor(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Reflection' : 'New Reflection'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Title (optional)</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                placeholder="Give this reflection a title..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select 
                  value={formData.reflection_type} 
                  onValueChange={(v) => setFormData(f => ({ ...f, reflection_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reflectionTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>How are you feeling?</Label>
                <Select 
                  value={formData.mood || ''} 
                  onValueChange={(v) => setFormData(f => ({ ...f, mood: v || null }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select mood..." />
                  </SelectTrigger>
                  <SelectContent>
                    {moods.map(mood => (
                      <SelectItem key={mood.value} value={mood.value}>
                        {mood.emoji} {mood.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Your Reflection</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(f => ({ ...f, content: e.target.value }))}
                placeholder="What's on your heart today?..."
                className="min-h-[200px]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { resetForm(); setShowEditor(false); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving || !formData.content.trim()}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2" /> Save</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
